/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

import React from "react";
import PropTypes from "prop-types";
import { Chip, IconButton, Tooltip } from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { formatDate } from "utils/DateTimeConversion";

const STATUS_STYLES = {
  paid: { label: "Paid", color: "#2E7D32", bg: "#E8F5E9" },
  pending: { label: "Pending", color: "#E68A06", bg: "#FFF3E0" },
  overdue: { label: "Overdue", color: "#C62828", bg: "#FDECEA" },
  cancelled: { label: "Cancelled", color: "#C62828", bg: "#FDECEA" },
};

export function scheduleRowLabel(row) {
  if (row?.payment_mode === "Installment" && row?.installment_number) {
    return `Installment ${row.installment_number}`;
  }
  return row?.payment_mode || "Payment";
}

function resolveStatusKey(status, dueDate) {
  const normalized = String(status || "").trim().toLowerCase();
  if (normalized === "paid") return "paid";
  if (normalized === "cancelled") return "cancelled";
  if (dueDate) {
    const due = new Date(dueDate);
    if (!Number.isNaN(due.getTime()) && due < new Date()) return "overdue";
  }
  return "pending";
}

function formatAmount(amount, currency) {
  const num = Number(amount || 0);
  const cur = (currency || "").trim();
  return cur ? `${cur} ${num.toFixed(2)}` : num.toFixed(2);
}

function statusChipLabel(status, statusKey) {
  if (statusKey === "paid") return "Paid";
  if (statusKey === "cancelled") return "Cancelled";
  if (statusKey === "overdue") return "Overdue";
  const text = String(status || "").trim();
  return text || "Pending";
}

function isPaidRow(status) {
  return String(status || "").trim().toLowerCase() === "paid";
}

function PaymentScheduleRow({
  label,
  invoiceNumber,
  onInvoiceClick,
  amount,
  currency,
  dueDate,
  status,
  highlighted,
  canResend,
  resendDisabledReason,
  onResend,
}) {
  const statusKey = resolveStatusKey(status, dueDate);
  const statusStyle = STATUS_STYLES[statusKey] || {
    label: statusChipLabel(status, statusKey),
    color: "#616161",
    bg: "#F0F0F0",
  };
  const chipLabel = statusChipLabel(status, statusKey);

  return (
    <MDBox
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      py={1}
      px={highlighted ? 1 : 0}
      sx={{
        borderBottom: "1px solid #EFF2F6",
        borderRadius: highlighted ? "6px" : 0,
        bgcolor: highlighted ? "#F0F7FF" : "transparent",
        gap: 1,
      }}
    >
      <MDBox flex={1} minWidth={0}>
        <MDTypography variant="button" fontWeight="medium" color="dark" display="block">
          {label}
        </MDTypography>
        {(invoiceNumber || dueDate) && (
            <MDTypography variant="caption" color="text" display="block">
              {invoiceNumber &&
                  (onInvoiceClick ? (
                      <>
                        Invoice{" "}
                        <MDTypography
                            component="span"
                            variant="caption"
                            color="info"
                            sx={{
                              cursor: "pointer",
                              fontWeight: 600,
                              "&:hover": { textDecoration: "underline" },
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onInvoiceClick();
                            }}
                        >
                          {invoiceNumber}
                        </MDTypography>
                      </>
                  ) : (
                      `Invoice ${invoiceNumber}`
                  ))}
              {invoiceNumber && dueDate && " · "}
              {dueDate && `Due ${formatDate(dueDate, "date")}`}
            </MDTypography>
        )}
      </MDBox>
      <MDBox display="flex" alignItems="center" gap={0.5} flexShrink={0}>
        <MDBox textAlign="right">
          <MDTypography variant="button" fontWeight="bold" color="dark" display="block">
            {formatAmount(amount, currency)}
          </MDTypography>
          <Chip
            label={chipLabel}
            size="small"
            sx={{
              bgcolor: statusStyle.bg,
              color: statusStyle.color,
              fontWeight: 700,
              fontSize: "0.65rem",
              height: 18,
              mt: 0.25,
            }}
          />
        </MDBox>
        {canResend && (
          <Tooltip title={resendDisabledReason || "Resend payment email"}>
            <span>
              <IconButton
                size="small"
                color="info"
                onClick={onResend}
                disabled={Boolean(resendDisabledReason)}
                aria-label="Resend payment email"
              >
                <EmailOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </MDBox>
    </MDBox>
  );
}

PaymentScheduleRow.propTypes = {
  label: PropTypes.string.isRequired,
  invoiceNumber: PropTypes.string,
  onInvoiceClick: PropTypes.func,
  amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  currency: PropTypes.string,
  dueDate: PropTypes.string,
  status: PropTypes.string,
  highlighted: PropTypes.bool,
  canResend: PropTypes.bool,
  resendDisabledReason: PropTypes.string,
  onResend: PropTypes.func,
};

export default function PaymentScheduleList({
  rows,
  highlightInvoiceId,
  showInvoiceNumber = true,
  onResendPaymentEmail,
  onInvoiceClick,
  resendDisabledReason = "",
}) {
  if (!rows?.length) return null;

  return (
    <MDBox>
      {rows.map((row) => {
        const unpaid = !isPaidRow(row.payment_status);
        const isCancelled = String(row.payment_status || "").trim().toLowerCase() === "cancelled";
        const showResend = Boolean(onResendPaymentEmail) && unpaid && !isCancelled && row.invoice_id;
        return (
          <PaymentScheduleRow
            key={
              row.payment_transaction_id ||
              `${row.invoice_id}-${row.payment_mode}-${row.installment_number || ""}`
            }
            label={scheduleRowLabel(row)}
            invoiceNumber={showInvoiceNumber ? row.invoice_number : null}
            onInvoiceClick={
              onInvoiceClick && row.invoice_id ? () => onInvoiceClick(row) : undefined
            }
            amount={row.amount}
            currency={row.currency}
            dueDate={row.payment_due_date}
            status={row.payment_status}
            highlighted={
              Boolean(row.is_current) ||
              (highlightInvoiceId != null && row.invoice_id === Number(highlightInvoiceId))
            }
            canResend={showResend}
            resendDisabledReason={resendDisabledReason}
            onResend={() => onResendPaymentEmail?.(row)}
          />
        );
      })}
    </MDBox>
  );
}

PaymentScheduleList.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object),
  highlightInvoiceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  showInvoiceNumber: PropTypes.bool,
  onResendPaymentEmail: PropTypes.func,
  onInvoiceClick: PropTypes.func,
  resendDisabledReason: PropTypes.string,
};
